/**
 * A customer's phone number, as an operator screen shows it.
 *
 * Three states, and the third is the reason this is a component rather than a `<span>`:
 *
 *   1. **Full.** The API sent the whole number. Shown whole, copied whole.
 *   2. **Masked.** The API sent only `maskedPhone`. Shown as the mask it is, **with a chip
 *      beside it saying so.** A mask presented as though it were the whole number is the exact
 *      failure this exists to prevent: an operator reads it down a phone line, the call does not
 *      connect, and nothing on the screen ever told them why.
 *   3. **None.** No phone on file at all, which is a real state — the credential columns are
 *      nullable so a legacy account can be migrated in place. Said in words rather than left as
 *      an empty cell, because an empty cell reads as a rendering bug.
 *
 * Nothing here ever unmasks. The digits are not in the response; anything that produced them
 * would be inventing them. See `lib/contact.ts`.
 */
import { CopyButton } from './ui.js';
import { phoneView, type PhoneBearing } from '../lib/contact.js';

export function ContactPhone({
  row,
  copy = true,
  compact = false,
}: {
  readonly row: PhoneBearing;
  /** Off in a dense table cell where a copy button per row would be more noise than help. */
  readonly copy?: boolean;
  /** Table mode: the chip shrinks to a dot and the explanation moves into the tooltip. */
  readonly compact?: boolean;
}): JSX.Element {
  const view = phoneView(row);

  if (view.source === 'none') {
    return <span className="muted">No phone on file</span>;
  }

  return (
    <span className="contact-phone">
      <span className="mono">{view.value}</span>
      {view.source === 'masked' && (
        <span
          className={`masked-tag${compact ? ' masked-tag--dot' : ''}`}
          title={view.note ?? undefined}
        >
          {/* The dot form still carries the full sentence in its tooltip and its accessible
              name, so the compact table cell says less on screen but never less in total. */}
          <span className={compact ? 'sr-only' : undefined}>Masked</span>
          {compact && <span aria-hidden>•</span>}
        </span>
      )}
      {copy && view.copyValue !== null && (
        <CopyButton value={view.copyValue} label={view.copyLabel} />
      )}
    </span>
  );
}

/**
 * The one-line explanation, for a detail view that has room for it.
 *
 * Separate from the chip above so a table can carry the chip alone and a definition list can
 * carry both. Renders nothing at all when the value is whole, which is the point: a console
 * that annotated every phone number would train an operator to stop reading the annotation.
 */
export function PhoneProvenance({ row }: { readonly row: PhoneBearing }): JSX.Element | null {
  const view = phoneView(row);
  if (view.note === null) return null;
  return <p className="contact-phone__note">{view.note}</p>;
}
